import { useEffect, useRef, useState, type CSSProperties } from 'react';
import svamiM from '../../levels/svami/obstacles/m.png';
import {
  SVAMI_OVERLAY_FADE_MS,
  SVAMI_OVERLAY_HOLD_MS,
} from './gameplayConstants';
import { SVAMI_OVERLAY_SPRITES } from './svamiOverlayTargets';
import './LevelSvamiOverlay.css';

type SvamiPhase = 'in' | 'hold' | 'out';

const PHASE_MS: Record<SvamiPhase, number> = {
  in: SVAMI_OVERLAY_FADE_MS,
  hold: SVAMI_OVERLAY_HOLD_MS,
  out: SVAMI_OVERLAY_FADE_MS,
};

const NEXT_PHASE: Record<SvamiPhase, SvamiPhase | null> = {
  in: 'hold',
  hold: 'out',
  out: null,
};

const SRC: Record<(typeof SVAMI_OVERLAY_SPRITES)[number]['srcKey'], string> = {
  svami_m: svamiM,
};

type LevelSvamiOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelSvamiOverlay({
  isPaused,
  onComplete,
}: LevelSvamiOverlayProps) {
  const [phase, setPhase] = useState<SvamiPhase>('in');
  const [shown, setShown] = useState(false);
  const remainingMsRef = useRef(PHASE_MS.in);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setShown(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'out') {
      setShown(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      remainingMsRef.current -= now - lastNow;
      lastNow = now;
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  return (
    <div
      className={
        shown ? 'level-svami-overlay level-svami-overlay--shown' : 'level-svami-overlay'
      }
      style={
        {
          transitionDuration: `${SVAMI_OVERLAY_FADE_MS}ms`,
        } as CSSProperties
      }
      aria-hidden
    >
      {SVAMI_OVERLAY_SPRITES.map((sprite) => (
        <img
          key={sprite.id}
          className="level-svami-overlay__sprite"
          src={SRC[sprite.srcKey]}
          alt=""
          style={{
            left: `${sprite.xPct}%`,
            top: `${sprite.yPct}%`,
            width: `${sprite.widthPct}%`,
          }}
        />
      ))}
    </div>
  );
}
